import { useContext } from 'react';
import getAccessToken from '../../../../../api/BearerToken';
import { useTranslation } from 'react-i18next';
import usePlatformName from '../../../../../hooks/usePlatformName';
import ModuleContext from '../../../_contexts/ModuleContext';
import IdentityContext from '../../../../../contexts/IdentityContext';
import { LtiSharedUserMaterial, LtiUserMaterial } from '../../../../../api/user-material';
import { getModuleById } from '../../../../../api/module';
import { buildLtiExerciseMaterial } from '../../../../../utils/user-material/buildLtiExerciseMaterial';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import { UserMaterialTags } from '../../../components/UserMaterialDialogHeaderTags';
import { LtiExerciseModal } from '../../../../../shared-components/elementis/components/lti-exercise-modal/LTIExerciseModal';

const tokenAccessor = { tokenGetter: getAccessToken };

const MODAL_ID = 'lti-user-material-modal';

const { REACT_APP_P_STUDIO_LANGUAGE, REACT_APP_LTI_AUTH_URL, REACT_APP_P_STUDIO_GROUP } = process.env;

const getTranslationsForLtiExerciseModal = (t: ReturnType<typeof useTranslation>['t']) => ({
  confirmationModals: {
    title: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.TITLE'),
    ariaLabel: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.A11Y_LABEL'),
    actions: {
      close: t('PAGES.TOC_LEVEL.MODALS.LTI.ACTIONS.CLOSE'),
      cancel: t('PAGES.TOC_LEVEL.MODALS.LTI.ACTIONS.CANCEL'),
      restart: t('PAGES.TOC_LEVEL.MODALS.LTI.ACTIONS.RESTART'),
    },
    body: {
      currentResult: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.CURRENT_RESULT'),
      score: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.SCORE'),
      percent: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.PERCENT'),
      notRepeatable: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.NOT_REPEATABLE'),
      repeatable: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.REPEATABLE'),
      exerciseTitle: t('PAGES.TOC_LEVEL.MODALS.LTI.CONFIRMATION.EXERCISE'),
    },
  },
  exerciseModal: {
    ariaLabel: t('PAGES.TOC_LEVEL.MODALS.LTI.A11Y_LABEL'),
  },
});

export default function useLtiUserMaterial() {
  const { t } = useTranslation();
  const platformName = usePlatformName();

  const { selectedModule, module } = useContext(ModuleContext);
  const { identity } = useContext(IdentityContext);
  const { nodes } = useContext(TableOfContentsContext);

  async function openLtiUserMaterial(
    material: LtiUserMaterial | LtiSharedUserMaterial,
    onModalClosed?: () => Promise<void>,
  ) {
    const materialModule = selectedModule || module || (await getModuleById(material.moduleId));

    if (!materialModule) return;

    return (
      <LtiExerciseModal
        id={MODAL_ID}
        ltipsUrl={REACT_APP_LTI_AUTH_URL}
        studioLanguage={REACT_APP_P_STUDIO_LANGUAGE}
        studioGroup={REACT_APP_P_STUDIO_GROUP}
        material={buildLtiExerciseMaterial(material)}
        module={materialModule}
        platformName={platformName}
        tokenAccessor={tokenAccessor}
        isOwner={identity.isTeacherAlike}
        onClose={() => onModalClosed?.()}
        translations={getTranslationsForLtiExerciseModal(t)}
        tags={<UserMaterialTags material={material} isTeacherAlike={identity.isTeacherAlike} nodes={nodes} />}
      />
    );
  }

  return { openLtiUserMaterial };
}
